import { rateCache } from './cache';

export interface CBNRate {
  currency: string;
  buyingRate: number;
  sellingRate: number;
  centralRate: number;
  lastUpdated: Date;
}

const FALLBACK_OFFICIAL_RATES: Record<string, number> = {
  USD: 1331.50,
  EUR: 1515.51,
  GBP: 1762.40,
  CAD: 975.00,
  AUD: 950.00,
  XOF: 2.31,
};

interface CBNApiRate {
  base?: string;
  quote?: string;
  rate?: number;
  date?: string;
}

function toRows(payload: unknown): CBNApiRate[] {
  if (Array.isArray(payload)) return payload as CBNApiRate[];
  if (payload && typeof payload === 'object') {
    const record = payload as { base?: string; date?: string; rates?: unknown; data?: unknown };
    if (Array.isArray(record.rates)) return record.rates as CBNApiRate[];
    if (Array.isArray(record.data)) return record.data as CBNApiRate[];
    if (record.rates && typeof record.rates === 'object') {
      return Object.entries(record.rates).map(([quote, rate]) => ({
        base: record.base ?? 'NGN',
        quote,
        rate: Number(rate),
        date: record.date,
      }));
    }
  }
  return [];
}

function toNairaRate(row: CBNApiRate): number {
  const base = row.base?.toUpperCase();
  const quote = row.quote?.toUpperCase();
  if (!base || !quote || !Number.isFinite(row.rate) || row.rate <= 0) return 0;
  if (quote === 'NGN') return row.rate;
  if (base === 'NGN') return 1 / row.rate;
  return 0;
}

function toCBNRate(currency: string, centralRate: number, lastUpdated = new Date()): CBNRate {
  return {
    currency,
    buyingRate: centralRate * 0.998,
    sellingRate: centralRate * 1.002,
    centralRate,
    lastUpdated,
  };
}

export async function getOfficialRates(): Promise<CBNRate[]> {
  const cached = rateCache.get('official-rates');
  if (cached) return cached;

  try {
    const response = await fetch('https://api.frankfurter.dev/v2/providers/cbn/rates');
    if (!response.ok) throw new Error(`CBN provider returned HTTP ${response.status}`);

    const rows = toRows(await response.json());
    const rates = rows
      .map(row => ({
        currency: (row.quote?.toUpperCase() === 'NGN' ? row.base : row.quote)?.toUpperCase(),
        centralRate: toNairaRate(row),
        lastUpdated: row.date ? new Date(row.date) : new Date(),
      }))
      .filter((row): row is { currency: string; centralRate: number; lastUpdated: Date } =>
        typeof row.currency === 'string' && row.currency !== 'NGN' && row.centralRate > 0,
      )
      .filter((row, index, all) => all.findIndex(candidate => candidate.currency === row.currency) === index)
      .map(row => toCBNRate(row.currency, row.centralRate, row.lastUpdated));

    if (rates.length === 0) throw new Error('CBN provider returned no usable rates');

    const liveByCurrency = new Map(rates.map(rate => [rate.currency, rate]));
    const completeRates = Object.entries(FALLBACK_OFFICIAL_RATES).map(([currency, fallbackRate]) =>
      liveByCurrency.get(currency) ?? toCBNRate(currency, fallbackRate),
    );
    rateCache.set('official-rates', completeRates, 86400000);
    return completeRates;
  } catch (error) {
    console.error('Error fetching CBN rates:', error);
    const fallback = Object.entries(FALLBACK_OFFICIAL_RATES).map(([currency, rate]) => toCBNRate(currency, rate));
    rateCache.set('official-rates', fallback, 300000);
    return fallback;
  }
}

export async function getOfficialRate(currency: string): Promise<number> {
  const rates = await getOfficialRates();
  return rates.find(rate => rate.currency === currency)?.centralRate ?? 0;
}
